import envData from './env'

let userId
let customerId
let isInElectron

const VO_NOTIFICATION_SUBSCRIPTION_URL =
  'jitsi:/vo-notification/v1/subscriptions'

const UPCOMING_EVENTS_FOR_DAYS = 3

export const init = ({ userInfo, isElectron }) => {
  ({ userId, customerId } = userInfo)
  isInElectron = isElectron
}

const EVENT_STATUSES = {
  NEEDS_ACTION: 'needs_action',
  ACCEPTED: 'accepted',
  DECLINED: 'declined',
  TENTATIVE: 'tentative',
  UNKNOWN: 'unknown'
}

const statusesParams = Object.values(EVENT_STATUSES).map(status => `status=${status}`).join('&')

const getConferenceAPI = (version = 1) => `${envData.vmmsUrl}/virtualmeeting/v${version}`

const getConferenceHistoryAPI = (version = 3) => `${envData.cloudflareApiUrl}/conference-history/v${version}`

const getCalendarAPI = () => `${isInElectron ? envData.cloud8Url : window.location.origin}/vos-calendar/api/cronofy`

const getCalendarAPIV3 = () => `${isInElectron ? envData.cloud8Url : window.location.origin}/vos-calendar/api/v3/cronofy`

const getConferenceMapperAPI = () => `${envData.conferenceMapperUrl}/v1`

const getPollsAPI = () => `${envData.cloudflareApiUrl}/polls-manager/v1/polls`

const getChatAPI = () => `${envData.cloudflareApiUrl}/vo-chat-history/v2`

const getTranscriptAPI = () => `${envData.cloudflareApiUrl}/transcriptions-store/v1`

const getContentSharingHistoryAPI = () => `${envData.cloudflareApiUrl}/vo-content-sharing-history/v1`

const getPermissionsAPI = () => `${envData.cloudflareApiUrl}/permissions-manager/v1`

const getSalesforceAPI = () => `${envData.cloudflareApiUrl}/salesforce-adapter`

const getSalesforceAuthAPI = () => `${getSalesforceAPI()}/oauth`

const getMeetingSummary = () => `${envData.cloudflareApiUrl}/meeting-summary/v1`

const getAppStoreAPI = () => `${envData.appStoreBaseUrl}/v1/_cfw/app-store/api/v1`

const getPasscodeAPI = () => `${envData.cloudflareApiUrl}/passcode/v1`

const getActiveDirectoryAPI = () => `${envData.cloudflareApiUrl}/v1/directory`

const URLS = {
  get token () {
    return `${envData.cloudflareApiUrl}/v1/meeting/token`
  },
  get personalConference () {
    return `${getConferenceMapperAPI()}/users/personal/conference`
  },
  get personalConferenceInvite () {
    return `${getConferenceMapperAPI()}/user/personal/invite`
  },
  get personalConferencePasscode () {
    return `${getConferenceMapperAPI()}/user/personal/conference/passcode`
  },
  get roomUrl () {
    return `${getConferenceMapperAPI()}/access/url/`
  },
  get profileUrl () {
    return `${getConferenceAPI()}/customers/${customerId}/profile`
  },
  get roomSettingsUrl () {
    return `${getConferenceAPI()}/customers/${customerId}/profile/conference/settings`
  },
  get userAddons () {
    return `${getPermissionsAPI()}/addons/user`
  },
  get meetMetricsAccess () {
    return `${getAppStoreAPI()}/user/customers/${customerId}/apps/${envData.meetMetricsAppId}/access`
  },
  get meetMetricsMfeEnabled () {
    return `${envData.meetMetricsApiUrl}/api/mfe/enabled`
  },
  get userPermissions () {
    return `${getPermissionsAPI()}/permissions/user`
  },
  get customerConferenceSettings () {
    return `${getConferenceAPI(2)}/customers/${customerId}/conference-settings`
  },
  get meetingProperties () {
    return `${getPasscodeAPI()}/conference-properties`
  },
  getMeetingProperties: meetingFqn => {
    return `${getPasscodeAPI()}/conference-properties?meetingFqn=${meetingFqn}`
  },
  sessionPolls: sessionId => `${getPollsAPI()}/${sessionId}`,
  sessionChat: sessionId =>
    `${getChatAPI()}/message-history/${sessionId}?messageType=CHAT`,
  sessionAI: sessionId =>
    `${getChatAPI()}/message-history/${sessionId}/transcription-ai`,
  sessionInvite: conferenceUrl =>
    `${getConferenceMapperAPI()}/user/scheduled/invite/full?conference=${conferenceUrl}`,
  updateMeetingProvider: provider =>
    `${getConferenceAPI()}/customers/${customerId}/profile/conference-provider?meetingProvider=${provider}`,
  recent: sessionId =>
    `${getConferenceHistoryAPI(3)}/conferences/${sessionId}?presign-recording-urls=false`,
  shareRecording: recordingId =>
    `${getConferenceHistoryAPI(1)}/shared-recording?recording-id=${recordingId}`,
  sharedFiles: ({ sessionId, pageIndex, pageSize }) =>
    `${getContentSharingHistoryAPI()}/documents/sessions/${sessionId}/files?offset=${pageIndex * pageSize}&size=${pageSize}`,
  recents: ({ maxResults, lastSessionId, maxSessionId }) => {
    const url = `${getConferenceHistoryAPI()}/users/self/conferences`
    const searchParams = new URLSearchParams()
    searchParams.append('presign-recording-urls', 'false')
    searchParams.append('speaker-stats', 'true')

    if (lastSessionId) {
      searchParams.append('last-session-id', lastSessionId)
    }

    if (maxSessionId) {
      searchParams.append('max-session-id', maxSessionId)
    }

    if (maxResults) {
      searchParams.append('max-results', maxResults)
    }

    return `${url}?${searchParams}`
  },
  screenshots: ({ sessionId, pageIndex, pageSize }) =>
    `${getContentSharingHistoryAPI()}/file-metadata/${sessionId}?offset=${pageIndex * pageSize}&page-size=${pageSize}`,
  contacts: ({ query, queryTypes = 'user' }) => {
    const searchParams = new URLSearchParams()
    searchParams.append('query', query)
    searchParams.append('queryTypes', queryTypes)

    return `${getActiveDirectoryAPI()}/search?${searchParams}`
  },
  get ANDROID_CALLBACK_URL () {
    return envData.androidCallbackUrl
  },
  get CHALET () {
    return `${VO_NOTIFICATION_SUBSCRIPTION_URL}/${encodeURIComponent(userId)}`
  },
  get FEEDBACK () {
    return envData.feedbackUrl
  },
  get CORS_PROXY () {
    return envData.corsProxyUrl
  },
  get JAAS () {
    return envData.jaasUrl
  },
  CALENDAR: {
    get LOGOUT () {
      return `${getCalendarAPI()}/auth/logout`
    },
    get cronofyAuthURL () {
      return `${getCalendarAPI()}/auth/authorize`
    },
    get STATUS () {
      return `${getCalendarAPI()}/auth/status`
    },
    get CALENDAR_LIST () {
      return `${getCalendarAPI()}/calendars`
    },
    get SETUP () {
      return `${getCalendarAPI()}/calendars/setup`
    },
    get EVENT_LIST () {
      return `${getCalendarAPIV3()}/events?days=${UPCOMING_EVENTS_FOR_DAYS}&${statusesParams}`
    },
    get UPDATE_EVENT () {
      return `${getCalendarAPI()}/events`
    },
    get SELECTED_CALENDARS () {
      return `${getCalendarAPI()}/profiles`
    }
  },

  TRANSCRIPT: {
    PHRASES (sessionId) {
      return `${getTranscriptAPI()}/transcriptions/${sessionId}`
    }
  },

  TEAM: {
    get MEMBERS () {
      return `${getConferenceAPI()}/standalone/team/members`
    }
  },

  SHARING_LINK (sessionId) {
    return `${getMeetingSummary()}/session/${sessionId}/sharing?scope=SHARED_ACCESS`
  },

  SALESFORCE: {
    get LOGIN_URL () {
      return `${getSalesforceAuthAPI()}/url`
    },

    get STATUS () {
      return `${getSalesforceAuthAPI()}/status-info`
    },

    get LOGOUT () {
      return `${getSalesforceAuthAPI()}/logout`
    },

    get URL () {
      return `${getSalesforceAPI()}/v1`
    },

    get RECENTS () {
      return `${getSalesforceAPI()}/v1/records/recents`
    },

    RECORDS ({ searchText }) {
      return `${getSalesforceAPI()}/v1/records?text=${searchText}`
    },

    SESSION_RECORDS ({ sessionId }) {
      return `${getSalesforceAPI()}/v1/sessions/${sessionId}/records`
    },

    RECORD_DETAILS ({ recordId, type }) {
      return `${getSalesforceAPI()}/v1/records/${recordId}?type=${type}`
    },

    UPDATE_SESSION ({ sessionId, recordId }) {
      return `${getSalesforceAPI()}/v1/sessions/${sessionId}/records/${recordId}`
    },

    get USER_INFO () {
      return `${getSalesforceAuthAPI()}/user`
    }
  }
}

export default URLS
